import React, { FC, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { Field, useField } from 'react-final-form';
import { isEmpty } from 'lodash';
import { useShopConfig } from '../../hooks/shopConfig';
import Heading from './Heading';
import { Button, ResponsiveImage, TypographyWrapper, ValidationError } from '../../components';
import ChooseCoverPhotoModal from './ChooseCoverPhotoModal';
import { UploadcareFile } from '../../types/uploadcare/file';
import CoverPhotoBackgroundRemovalModal from '../../components/BackgroundRemovalModal/CoverPhotoBackgroundRemovalModal';
import { BackgroundRemovalType } from '../../types/uploadcare/backgroundRemovalTypes';
import css from './ChooseCoverPhotoModal.module.css';
import { required } from '../../util/validators';
import { ListingFlowFormField } from './ListingFlowTypes';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useIsBackgroundRemovalEnabled } from './hooks/useIsBackgroundRemovalEnabled';
import { ModalType } from '../../ducks/modal.duck';
import { useActiveModal } from '../../hooks/useActiveModal';
import { useIsBackgroundRemovalType } from './hooks/useBackgroundRemovalType';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import { TypographyFormat } from '../../components/TypographyWrapper/TypographyWrapper';

interface ChooseCoverPhotoProps {
  isUploading: boolean;
  hasUploadError: boolean;
  imagesFormValue: UploadcareFile[];
  onModalSubmit: (image: UploadcareFile, imageBase64?: string) => Promise<any> | void;
  onBackgroundRemovalModalClose?: () => void;
}

const ChooseCoverPhoto: FC<ChooseCoverPhotoProps> = (props: ChooseCoverPhotoProps) => {
  const {
    isUploading,
    hasUploadError,
    imagesFormValue,
    onModalSubmit,
    onBackgroundRemovalModalClose,
  } = props;

  const {
    isModalOpen: isBackgroundRemovalModalOpen,
    openModal: openBackgroundRemovalModal,
    closeModal: closeBackgroundRemovalModal,
  } = useActiveModal(ModalType.CoverPhotoBackgroundRemoval);
  const {
    isModalOpen: isChooseCoverPhotoModalOpen,
    openModal: openChooseCoverPhotoModal,
    closeModal: closeChooseCoverPhotoModal,
  } = useActiveModal(ModalType.ChooseCoverPhoto);

  const { treetShopName, imageRatio } = useShopConfig();
  const isBackgroundRemovalEnabled = useIsBackgroundRemovalEnabled();
  const { isBrand } = useCurrentUserPermissions();

  const featuredImageID = useField(ListingFlowFormField.FeaturedImageId)?.input?.value;
  const featuredImage = imagesFormValue.find(
    (image: UploadcareFile) => image.uuid === featuredImageID
  );
  const [selectedCoverPhotoInModal, setSelectedCoverPhotoInModal] = useState<UploadcareFile>(
    featuredImage || imagesFormValue[0]
  );
  const { bgRemovalType } = useIsBackgroundRemovalType();

  const isMobile = useIsMobile();

  useEffect(() => {
    if (featuredImage) setSelectedCoverPhotoInModal(featuredImage);
  }, [featuredImageID]);

  const helperText = `Your cover photo will be used to represent your item across ${treetShopName}.`;
  const isCoverPhotoBgRemovalRequired =
    isBackgroundRemovalEnabled &&
    !isBrand &&
    (bgRemovalType === BackgroundRemovalType.CoverRequired ||
      bgRemovalType === BackgroundRemovalType.CoverRequiredOthersOptional);

  const headerHelperText = isBrand
    ? `${helperText} Note that background removal is not required for photos uploaded by brands.`
    : isCoverPhotoBgRemovalRequired
    ? `${helperText} Note that background removal is required for your cover photo.`
    : helperText;

  const coverPhotoModalHelperText = isCoverPhotoBgRemovalRequired
    ? 'Background removal is required for your cover photo. Make sure to choose a photo taken against a plain background.'
    : undefined;

  if (isEmpty(imagesFormValue)) return null;

  const buildImageWrapperStyle = () => {
    const maxWidth = 250;

    return isMobile
      ? {
          width: `${maxWidth}px`,
          height: `${maxWidth * imageRatio}px`,
        }
      : {
          width: '100vw',
          height: `${100 * imageRatio}vw`,
          maxWidth: `${maxWidth}px`,
          maxHeight: `${maxWidth * imageRatio}px`,
          cursor: 'pointer',
        };
  };

  const handleCoverPhotoModalSubmit = () => {
    if (selectedCoverPhotoInModal.isBackgroundRemoved || !isCoverPhotoBgRemovalRequired) {
      onModalSubmit(selectedCoverPhotoInModal);
    } else {
      openBackgroundRemovalModal();
    }
  };

  const handleBackgroundRemovalModalSubmit = async (bgRemovedImageBase64?: string) => {
    if (!selectedCoverPhotoInModal) {
      return;
    }
    if (bgRemovedImageBase64) {
      await onModalSubmit(selectedCoverPhotoInModal, bgRemovedImageBase64);
      closeBackgroundRemovalModal();
    }
  };

  return (
    <>
      <Box mb={1}>
        <Heading>Choose Cover Photo</Heading>
      </Box>
      <Box mb={1}>
        <TypographyWrapper variant="body1">{headerHelperText}</TypographyWrapper>
      </Box>
      <Field
        name={ListingFlowFormField.FeaturedImageId}
        validate={required('Choose a cover photo.')}
      >
        {({ input, meta }) => (
          <>
            <input
              {...input}
              id={ListingFlowFormField.FeaturedImageId}
              className={css.hiddenInput}
              type="hidden"
            />
            <ValidationError fieldMeta={meta} />
          </>
        )}
      </Field>
      <button
        type="button"
        className={css.coverPhotoTextButton}
        onClick={openChooseCoverPhotoModal}
      >
        {featuredImage?.cdnUrl ? (
          <ResponsiveImage
            className={css.imageContainer}
            alt="cover photo"
            src={featuredImage?.cdnUrl}
          />
        ) : (
          <div className={css.noImageContainer}>
            <div className={css.noImageWrapper} style={buildImageWrapperStyle()}>
              <Button className={css.coverPhotoActionButton}>Choose Cover Photo</Button>
            </div>
          </div>
        )}
        {featuredImage?.cdnUrl && (
          <Box pt={1}>
            <TypographyWrapper variant="body1" format={TypographyFormat.HoverUnderlined}>
              Change Cover Photo
            </TypographyWrapper>
          </Box>
        )}
      </button>
      <ChooseCoverPhotoModal
        images={imagesFormValue}
        isOpen={isChooseCoverPhotoModalOpen}
        onClose={closeChooseCoverPhotoModal}
        onCoverPhotoSelected={(image: UploadcareFile) => setSelectedCoverPhotoInModal(image)}
        onSubmit={() => {
          closeChooseCoverPhotoModal();
          handleCoverPhotoModalSubmit();
        }}
        selectedImageUuid={selectedCoverPhotoInModal?.uuid}
        modalHelperText={coverPhotoModalHelperText}
      />
      {selectedCoverPhotoInModal && (
        <CoverPhotoBackgroundRemovalModal
          originalImageSrc={selectedCoverPhotoInModal.cdnUrl}
          open={isBackgroundRemovalModalOpen}
          onClose={() => {
            if (onBackgroundRemovalModalClose) onBackgroundRemovalModalClose();
            closeBackgroundRemovalModal();
          }}
          isUploading={isUploading}
          onCoverPhotoBackgroundRemovalModalSubmit={handleBackgroundRemovalModalSubmit}
          hasUploadError={hasUploadError}
        />
      )}
    </>
  );
};

export default ChooseCoverPhoto;
